import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiLibraryModule } from "@template-ui/template-ui.module";

import { AppBaseComponent } from "@app/shared/components/app-base.component";
import { PhmsIntakeRuntimeService } from "../phms-intake-runtime.service";
import {
    PhmsIntakeDisplayError,
    PhmsIntakeInterfaceIngestRequest,
    PhmsIntakeReconciliationParsedRecord,
    PhmsIntakeReconciliationRunDto,
    mapPhmsIntakeError,
} from "./phms-intake-reconciliation.models";
import { PhmsIntakeReconciliationService } from "./phms-intake-reconciliation.service";

const PHMS_INTAKE_SUBMIT = "phms.intake.submit";

@Component({
    selector: "phms-intake-reconciliation",
    standalone: true,
    imports: [CommonModule, UiLibraryModule],
    templateUrl: "./phms-intake-reconciliation.component.html",
    styleUrl: "./phms-intake-reconciliation.component.scss",
})
export class PhmsIntakeReconciliationComponent extends AppBaseComponent {
    private readonly runtime = inject(PhmsIntakeRuntimeService);
    private readonly reconciliationService = inject(PhmsIntakeReconciliationService);

    readonly canSubmit = this.roles.hasAnyPermissionSignal(PHMS_INTAKE_SUBMIT);

    readonly isIngesting = signal(false);
    readonly isLoadingRun = signal(false);
    readonly programScopeKeysLoaded = signal(false);
    readonly sourceSystemKey = signal("");
    readonly programScopeKey = signal("");
    readonly selectedFileName = signal<string | null>(null);
    readonly loadRunId = signal("");
    readonly run = signal<PhmsIntakeReconciliationRunDto | null>(null);
    readonly successMessage = signal<string | null>(null);
    readonly errorState = signal<PhmsIntakeDisplayError | null>(null);

    private parsedRecords: PhmsIntakeReconciliationParsedRecord[] = [];

    readonly nameLabel = this.runtime.incarceratedPersonDisplayNameLabel;
    readonly identifierLabel = this.runtime.incarceratedPersonIdentifierLabel;
    readonly programScopeKeyLabel = this.runtime.programScopeKeyLabel;
    readonly programScopeKeyOptions = this.runtime.programScopeKeyOptions;

    readonly hasProgramScopeKeyOptions = computed(() => this.programScopeKeyOptions().length > 0);
    readonly hasRun = computed(() => this.run() !== null);
    readonly attentionRequiredCount = computed(() => this.run()?.attentionRequiredCount ?? 0);
    readonly hasAttentionRequired = computed(() => this.attentionRequiredCount() > 0);

    readonly pageTitle = "PHMS Interface Intake & Reconciliation Review";
    readonly pageSubtitle =
        "Ingest a single-source interface feed and review the reconciliation run. Server-side authorization remains authoritative.";

    constructor() {
        super();
        this.runtime
            .loadProgramScopeKeyOptions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.programScopeKeysLoaded.set(true),
                error: () => this.programScopeKeysLoaded.set(true),
            });

        this.runtime.loadFieldLabels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    onSourceSystemKeyChange(value: string): void {
        this.sourceSystemKey.set(value);
    }

    onProgramScopeKeyChange(value: string): void {
        this.programScopeKey.set(value);
    }

    onLoadRunIdChange(value: string): void {
        this.loadRunId.set(value);
    }

    onFileSelected(event: Event): void {
        this.clearMessages();
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            this.selectedFileName.set(null);
            this.parsedRecords = [];
            return;
        }

        this.selectedFileName.set(file.name);
        const reader = new FileReader();
        reader.onload = () => {
            try {
                this.parsedRecords = parseCsv(typeof reader.result === "string" ? reader.result : "");
                if (this.parsedRecords.length === 0) {
                    this.errorState.set({
                        title: "No records found",
                        messages: ["The selected file did not contain any data rows."],
                        issueCodes: [],
                    });
                }
            } catch {
                this.parsedRecords = [];
                this.errorState.set({
                    title: "File could not be parsed",
                    messages: ["The selected file could not be read as CSV. Verify the header row and try again."],
                    issueCodes: [],
                });
            }
        };
        reader.onerror = () => {
            this.parsedRecords = [];
            this.errorState.set({
                title: "File could not be read",
                messages: ["The selected file could not be read. Try again or choose a different file."],
                issueCodes: [],
            });
        };
        reader.readAsText(file);
    }

    ingest(): void {
        if (!this.guardPermission()) {
            return;
        }

        this.clearMessages();

        const sourceKey = this.sourceSystemKey().trim();
        if (!sourceKey) {
            this.errorState.set({
                title: "Source system required",
                messages: ["Enter the governed source system key before ingesting."],
                issueCodes: [],
            });
            return;
        }

        const scopeKey = this.programScopeKey().trim();
        if (!scopeKey) {
            this.errorState.set({
                title: "Program scope required",
                messages: ["Select a program scope before ingesting."],
                issueCodes: [],
            });
            return;
        }

        if (this.parsedRecords.length === 0) {
            this.errorState.set({
                title: "No records to ingest",
                messages: ["Choose a CSV file with at least one data row before ingesting."],
                issueCodes: [],
            });
            return;
        }

        const request: PhmsIntakeInterfaceIngestRequest = {
            sourceSystemKey: sourceKey,
            records: this.parsedRecords.map((record) => ({
                incarceratedPersonDisplayName: record.incarceratedPersonDisplayName,
                incarceratedPersonIdentifier: record.incarceratedPersonIdentifier,
                programScopeKey: record.programScopeKey || scopeKey,
                sourceRecordKey: record.sourceRecordKey || undefined,
            })),
        };

        this.isIngesting.set(true);
        this.reconciliationService
            .ingest(sourceKey, request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.isIngesting.set(false);
                    this.run.set(result);
                    this.successMessage.set(
                        `Reconciliation run #${result.id} ${result.runStatusCode}: ` +
                            `${result.insertedCount} inserted, ${result.matchedCount} matched, ` +
                            `${result.conflictCount} conflict(s), ${result.attentionRequiredCount} needing review.`,
                    );
                },
                error: (error: unknown) => {
                    this.isIngesting.set(false);
                    this.errorState.set(mapPhmsIntakeError(error));
                },
            });
    }

    loadRun(): void {
        if (!this.guardPermission()) {
            return;
        }

        this.clearMessages();

        const sourceKey = this.sourceSystemKey().trim();
        if (!sourceKey) {
            this.errorState.set({
                title: "Source system required",
                messages: ["Enter the governed source system key before loading a run."],
                issueCodes: [],
            });
            return;
        }

        const runId = Number(this.loadRunId().trim());
        if (!Number.isInteger(runId) || runId <= 0) {
            this.errorState.set({
                title: "Run id required",
                messages: ["Enter a valid reconciliation run id to load."],
                issueCodes: [],
            });
            return;
        }

        this.isLoadingRun.set(true);
        this.reconciliationService
            .getRun(sourceKey, runId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.isLoadingRun.set(false);
                    this.run.set(result);
                    this.successMessage.set(`Loaded reconciliation run #${result.id}.`);
                },
                error: (error: unknown) => {
                    this.isLoadingRun.set(false);
                    this.errorState.set(mapPhmsIntakeError(error));
                },
            });
    }

    private guardPermission(): boolean {
        if (this.canSubmit()) {
            return true;
        }

        this.errorState.set({
            title: "Access denied",
            messages: ["You do not have permission to submit intake submissions."],
            issueCodes: [],
        });
        return false;
    }

    private clearMessages(): void {
        this.successMessage.set(null);
        this.errorState.set(null);
    }

    attentionStateLabel(value: string): string {
        return value === "needs_review" ? "Needs review" : "Clear";
    }
}

const HEADER_ALIASES: Record<string, keyof PhmsIntakeReconciliationParsedRecord> = {
    incarceratedpersondisplayname: "incarceratedPersonDisplayName",
    displayname: "incarceratedPersonDisplayName",
    name: "incarceratedPersonDisplayName",
    incarceratedpersonidentifier: "incarceratedPersonIdentifier",
    identifier: "incarceratedPersonIdentifier",
    id: "incarceratedPersonIdentifier",
    programscopekey: "programScopeKey",
    programscope: "programScopeKey",
    scope: "programScopeKey",
    sourcerecordkey: "sourceRecordKey",
    sourcerecord: "sourceRecordKey",
    recordkey: "sourceRecordKey",
};

export function parseCsv(content: string): PhmsIntakeReconciliationParsedRecord[] {
    const lines = content
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    if (lines.length === 0) {
        return [];
    }

    const headerCells = splitCsvLine(lines[0]).map((cell) =>
        cell.toLowerCase().replace(/[^a-z0-9]/g, ""),
    );
    const columnMap = headerCells.map((cell) => HEADER_ALIASES[cell]);

    return lines.slice(1).map((line) => {
        const cells = splitCsvLine(line);
        const record: PhmsIntakeReconciliationParsedRecord = {
            incarceratedPersonDisplayName: "",
            incarceratedPersonIdentifier: "",
            programScopeKey: "",
            sourceRecordKey: "",
        };
        columnMap.forEach((field, index) => {
            if (field) {
                record[field] = (cells[index] ?? "").trim();
            }
        });
        return record;
    });
}

function splitCsvLine(line: string): string[] {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        if (char === '"') {
            if (inQuotes && line[index + 1] === '"') {
                current += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === "," && !inQuotes) {
            cells.push(current);
            current = "";
        } else {
            current += char;
        }
    }
    cells.push(current);
    return cells;
}
